
import {
  SCHEMA_TYPES,
  DOCUMENT_TYPES,
  RETRIEVAL_OPTIONS,
  LLM_MODEL_OPTIONS,
  validatePageRange,
  validateTemplateName,
  validateTemplateFieldsArray,
} from "./constants";
import { ENGINE_OPTIONS } from "./OcrEngineModal";

// Keys the main "Save" button already persists — the only ones a Replace-from-JSON
// import is allowed to touch. Slug and page range have their own independent save
// paths and must never be silently overwritten by an import.
export const IMPORTABLE_TEMPLATE_KEYS = [
  "name",
  "document_type",
  "extraction_engine",
  "retrieval_strategy",
  "llm_model_key",
  "fields",
];

const ENGINE_VALUES = ENGINE_OPTIONS.map((o) => o.value);
const RETRIEVAL_VALUES = RETRIEVAL_OPTIONS.map((o) => o.value);
const MODEL_VALUES = LLM_MODEL_OPTIONS.map((o) => o.value);

const isPlainObject = (v) => typeof v === "object" && v !== null && !Array.isArray(v);

// ─── Parse ───────────────────────────────────────────────────────────────────

export const parseTemplateJson = (text) => {
  if (!text?.trim()) return { data: null, error: "Paste or upload a JSON template first." };
  try {
    const data = JSON.parse(text);
    if (!isPlainObject(data)) {
      return { data: null, error: "Template JSON must be an object, not an array or primitive." };
    }
    return { data, error: null };
  } catch (err) {
    return { data: null, error: `Invalid JSON: ${err.message}` };
  }
};

// ─── Validate ────────────────────────────────────────────────────────────────

const validateSchemaNode = (node, path, errors) => {
  if (node == null) return;
  if (!isPlainObject(node) || node.type !== "object" || !Array.isArray(node.properties)) {
    errors.push(`${path}: schema_definition must be shaped { type: "object", properties: [...] }.`);
    return;
  }
  node.properties.forEach((prop, i) => {
    const propPath = `${path}.properties[${i}]`;
    if (!prop?.key || !String(prop.key).trim()) {
      errors.push(`${propPath}: missing "key".`);
    }
    if (!SCHEMA_TYPES.includes(prop?.type)) {
      errors.push(`${propPath}${prop?.key ? ` (${prop.key})` : ""}: unknown or missing "type" — must be one of ${SCHEMA_TYPES.join(", ")}.`);
      return;
    }
    if (prop.type === "object") {
      if (!Array.isArray(prop.properties)) {
        errors.push(`${propPath} (${prop.key}): type "object" requires a "properties" array.`);
      } else {
        validateSchemaNode({ type: "object", properties: prop.properties }, propPath, errors);
      }
    }
    if (prop.type === "table") {
      if (!isPlainObject(prop.items) || prop.items.type !== "object" || !Array.isArray(prop.items.properties)) {
        errors.push(`${propPath} (${prop.key}): type "table" requires an "items" object shaped { type: "object", properties: [...] }.`);
      } else {
        validateSchemaNode(prop.items, `${propPath}.items`, errors);
      }
    }
  });
};

// mode: "create" (Templates Dashboard → Import Template) or "replace" (Workspace → Replace from JSON)
export const validateTemplateJson = (data, { mode = "create" } = {}) => {
  if (!isPlainObject(data)) return ["Template JSON must be an object."];

  const errors = [];

  if (mode === "create") {
    const nameError = validateTemplateName(data.name);
    if (nameError) errors.push(nameError);

    if (!data.extraction_engine) {
      errors.push(`extraction_engine is required — choose one of ${ENGINE_VALUES.join(", ")}.`);
    }

    const pageRangeError = validatePageRange(data.start_page ?? null, data.end_page ?? null);
    if (pageRangeError) errors.push(pageRangeError);
  }

  if (data.document_type != null && !DOCUMENT_TYPES.includes(data.document_type)) {
    errors.push(`Unknown document_type "${data.document_type}" — must be one of ${DOCUMENT_TYPES.join(", ")}.`);
  }
  if (data.extraction_engine != null && !ENGINE_VALUES.includes(data.extraction_engine)) {
    errors.push(`Unknown extraction_engine "${data.extraction_engine}" — must be one of ${ENGINE_VALUES.join(", ")}.`);
  }
  if (data.retrieval_strategy != null && !RETRIEVAL_VALUES.includes(data.retrieval_strategy)) {
    errors.push(`Unknown retrieval_strategy "${data.retrieval_strategy}" — must be one of ${RETRIEVAL_VALUES.join(", ")}.`);
  }
  if (data.llm_model_key != null && !MODEL_VALUES.includes(data.llm_model_key)) {
    errors.push(`Unknown llm_model_key "${data.llm_model_key}" — must be one of ${MODEL_VALUES.join(", ")}.`);
  }
  if (data.fields != null && !Array.isArray(data.fields)) {
    errors.push('"fields" must be an array.');
  }

  const fields = Array.isArray(data.fields) ? data.fields : [];
  errors.push(...validateTemplateFieldsArray(fields));

  fields.forEach((f, idx) => {
    const label = `Field ${idx + 1}${f?.field_key ? ` (${f.field_key})` : ""}`;
    if (f?.data_type != null && !SCHEMA_TYPES.includes(f.data_type)) {
      errors.push(`${label}: unknown data_type "${f.data_type}" — must be one of ${SCHEMA_TYPES.join(", ")}.`);
      return;
    }
    if (f?.data_type === "object") {
      if (!f.schema_definition) {
        errors.push(`${label}: type "object" requires a schema_definition.`);
      } else {
        validateSchemaNode(f.schema_definition, label, errors);
      }
    } else if (f?.data_type === "table" && f.schema_definition) {
      validateSchemaNode(f.schema_definition, label, errors);
    }
  });

  return errors;
};

// ─── Summarize (for the review screen) ────────────────────────────────────────

export const summarizeTemplateJson = (data) => {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const seen = new Set();
  const duplicateKeys = new Set();
  fields.forEach((f) => {
    const key = (f?.field_key || "").trim().toLowerCase();
    if (!key) return;
    if (seen.has(key)) duplicateKeys.add(key);
    else seen.add(key);
  });

  return {
    fieldCount: fields.length,
    tableCount: fields.filter((f) => f?.data_type === "table").length,
    objectCount: fields.filter((f) => f?.data_type === "object").length,
    missingInstructionCount: fields.filter((f) => !f?.instruction?.trim()).length,
    duplicateKeys: Array.from(duplicateKeys),
  };
};

// ─── Normalize (fill defaults, ready to merge into template state or POST) ────

const normalizeField = (f, idx) => ({
  field_key: (f?.field_key || "").trim(),
  data_type: f?.data_type || "text",
  instruction: f?.instruction || "",
  output_rules: f?.output_rules || "",
  field_order: f?.field_order ?? idx + 1,
  system_mapping: f?.system_mapping || "",
  schema_definition: f?.schema_definition ?? null,
});

export const normalizeTemplateJson = (data, { includePageRange = false } = {}) => {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const normalized = {
    name: (data?.name || "").trim(),
    document_type: DOCUMENT_TYPES.includes(data?.document_type) ? data.document_type : "custom",
    extraction_engine: ENGINE_VALUES.includes(data?.extraction_engine) ? data.extraction_engine : null,
    retrieval_strategy: RETRIEVAL_VALUES.includes(data?.retrieval_strategy) ? data.retrieval_strategy : "full_text",
    llm_model_key: MODEL_VALUES.includes(data?.llm_model_key) ? data.llm_model_key : "gemini_2_5_flash_lite",
    fields: fields.map(normalizeField),
  };
  if (includePageRange) {
    normalized.start_page = data?.start_page ?? null;
    normalized.end_page = data?.end_page ?? null;
  }
  return normalized;
};

// ─── Export ────────────────────────────────────────────────────────────────────

export const buildExportableTemplateJson = (template) => {
  const normalized = normalizeTemplateJson(template, { includePageRange: true });
  return JSON.stringify(normalized, null, 2);
};

export const downloadTemplateJson = (template) => {
  const json = buildExportableTemplateJson(template);
  const blob = new Blob([json], { type: "application/json;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const safeName = (template?.name || "template").replace(/[^a-z0-9]/gi, "_");

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${safeName}.json`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
